import { useState } from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import type { PaymentWalletOption, Stripe, StripeElementsUpdateOptions } from '@stripe/stripe-js'
import { PaymentElement, useElements } from '@stripe/react-stripe-js'
import withForm from 'contexts/withForm'
import { rem } from 'polished'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { updateStripeOffSessionCardByRegionAndPurposeId } from 'api/payment'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { useIntl } from 'react-intl'

const StripeElement = styled(PaymentElement)`
  border: none;
`

const StyledBodyText = styled(BodyText)`
margin-top: ${rem(24)};
`

const StylesLineInfoCircleIcon = styled(LineInfoCircleIcon)`
margin-left: -${rem(2)};
margin-right: ${rem(5)};
`

interface Props {
  chargeDescriptors: Record<string, string>
  stripe: Stripe
  regionCode: string
  currencyCode: string
  purpose: string
  purposeId: string
  onSuccess?: () => void
  onFailure?: () => void
}

function StripeWidgets(props: Props) {
  const intl = useIntl()
  const {
    regionCode,
    currencyCode,
    stripe,
    purpose,
    purposeId,
    onSuccess,
    onFailure,
  } = props

  const [isUpdatingPaymentMethod, setIsUpdatingPaymentMethod] = useState(false)
  const elements = useElements()

  const updateStripeOffSessionCard = async() => {
    try {
      if (!elements || !stripe) return

      setIsUpdatingPaymentMethod(true)

      elements.update({ setupFutureUsage: 'off_session' } as StripeElementsUpdateOptions)
      const { error: submitError } = await elements.submit()

      if (submitError) {
        throw new Error(submitError.message)
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        elements,
      })

      if (error) {
        throw new Error(error.message)
      }

      const response = await updateStripeOffSessionCardByRegionAndPurposeId(regionCode, purposeId, {
        currency: currencyCode,
        brand: config.BRAND,
        paymentMethodType: 'card',
        metadata: {
          paymentMethodId: paymentMethod.id,
          purpose,
        },
      })

      const clientSecret = response.clientSecret

      const setupParams = {
        clientSecret,
        redirect: 'if_required',
      }

      // @ts-ignore
      const { error: stripeError } = await stripe.confirmSetup(setupParams)
      if (stripeError) {
        setIsUpdatingPaymentMethod(false)
        onFailure?.()
        return
      }
      await new Promise((resolve) => setTimeout(resolve, 3000))
      setIsUpdatingPaymentMethod(false)
      onSuccess?.()
    } catch {
      setIsUpdatingPaymentMethod(false)
      onFailure?.()
    }
  }

  const stripePaymentElementOptions = {
    layout: 'tabs' as const,
    wallets: {
      applePay: 'never' as PaymentWalletOption,
      googlePay: 'never' as PaymentWalletOption,
    },
  }

  const cardSaveMessage = intl.formatMessage({
    id: 'ivGfIzRHZqS17IAMlek0',
    defaultMessage: 'Your card will be saved for your {programName} subscriptions',
    description: 'Message about card being saved for LuxPlus subscriptions',
  }, { programName: LUXURY_PLUS.PROGRAM_NAME })

  const updateButtonLabel = intl.formatMessage({
    id: 'FmR2BvFOt9kAHdr-LimK',
    defaultMessage: 'Update',
    description: 'Button text to update payment method during the checkout and payment process',
  })

  return (
    <>
      <>
        <StripeElement options={stripePaymentElementOptions} />
        <StyledBodyText variant="medium">
          <StylesLineInfoCircleIcon display="inline" size="S" />
          {cardSaveMessage}
        </StyledBodyText>
      </>
      <Group direction="horizontal" horizontalAlign="end" gap={20}>
        <TextButton
          kind="primary"
          data-testid="pay-now"
          size="large"
          fit="mobile-full-width"
          id="payment-button"
          onClick={updateStripeOffSessionCard}
          disabled={isUpdatingPaymentMethod}
        >
          {updateButtonLabel}
        </TextButton>
      </Group>
    </>
  )
}

const mapStateToProps = (state: App.State) => ({
  utm: state.utm,
  account: state.auth.account,
  stripePaymentMethod: state.checkout.stripePaymentMethod,
})

export default withForm(
  connect(mapStateToProps, null)(StripeWidgets),
)
